import { FC, useCallback, useState } from 'react';
import classNames from 'classnames';

import { Heading, Pressable, Button } from '../../../shared/ui';

import { availableFigures, Figure, PlacedFigure } from './types';
import { FigureRenderer } from './FigureRenderer';
import { SandTherapyBoard } from './SandTherapyBoard';

function getFigureHeightClass(size: Figure['size']) {
  switch (size) {
    case 'small':
      return 'h-10';
    case 'medium':
      return 'h-14';
    case 'large':
      return 'h-16';
  }
}

interface SandTherapyClientProps {
  onSelectFigures?: (figures: Figure[]) => void;
  onStartPlacement?: () => void;
  onPlaceFigure?: (figure: PlacedFigure) => void;
  onMoveFigure?: (id: string, x: number, y: number) => void;
  onRotateFigure?: (id: string) => void;
  onResizeFigure?: (id: string, scale: number) => void;
  onRemoveFigure?: (id: string) => void;
  onSandColorChange?: (color: string) => void;
  onClientReady?: () => void;
  placedFigures: PlacedFigure[];
  sandColor: string;
  clientReady: boolean;
  /** Мобильный 1:1 на весь экран: заголовок в верхней полосе сцены, поле на касаниях, без фиксированных 1200px */
  mobile?: boolean;
}

export const SandTherapyClient: FC<SandTherapyClientProps> = ({
  onSelectFigures,
  onStartPlacement,
  onPlaceFigure,
  onMoveFigure,
  onRotateFigure,
  onResizeFigure,
  onRemoveFigure,
  onSandColorChange,
  onClientReady,
  placedFigures,
  sandColor,
  clientReady,
  mobile = false,
}) => {
  const [phase, setPhase] = useState<'select' | 'place'>('select');
  const [selectedFigures, setSelectedFigures] = useState<Figure[]>([]);

  const toggleFigure = useCallback((fig: Omit<Figure, 'id'>) => {
    setSelectedFigures((prev) => {
      const existing = prev.findIndex((f) => f.type === fig.type && f.color === fig.color && f.size === fig.size);
      if (existing >= 0) {
        return prev.filter((_, i) => i !== existing);
      }
      return [...prev, { ...fig, id: `${fig.type}-${fig.color}-${Date.now()}-${Math.random()}` }];
    });
  }, []);

  const isFigureSelected = useCallback(
    (fig: Omit<Figure, 'id'>) =>
      selectedFigures.some((f) => f.type === fig.type && f.color === fig.color && f.size === fig.size),
    [selectedFigures],
  );

  const handleStartPlacement = useCallback(() => {
    onSelectFigures?.(selectedFigures);
    onStartPlacement?.();
    setPhase('place');
  }, [selectedFigures, onSelectFigures, onStartPlacement]);

  if (phase === 'place' || clientReady) {
    return (
      <div
        className={classNames('h-full w-full flex flex-col bg-ink-muted text-white', mobile ? 'px-3 pb-3 pt-1' : 'p-4')}
      >
        {!mobile && (
          <Heading tone="inverse" size="md">
            Песочная терапия
          </Heading>
        )}
        <p className="text-white/70 text-ui-xs mb-3">
          {mobile
            ? 'Расставьте фигуры на поле: перетаскивайте их пальцем, выбранную фигуру можно повернуть, изменить размер или удалить.'
            : 'Расставьте фигуры на поле. Перетаскивайте их для изменения положения.'}
        </p>
        <div className="flex-1 min-h-0">
          <SandTherapyBoard
            mobile={mobile}
            selectedFigures={selectedFigures}
            placedFigures={placedFigures}
            onPlaceFigure={(fig) => onPlaceFigure?.(fig)}
            onMoveFigure={(id, x, y) => onMoveFigure?.(id, x, y)}
            onRotateFigure={(id) => onRotateFigure?.(id)}
            onResizeFigure={(id, scale) => onResizeFigure?.(id, scale)}
            onRemoveFigure={(id) => onRemoveFigure?.(id)}
            onSandColorChange={onSandColorChange}
            onClientReady={onClientReady}
            sandColor={sandColor}
            clientReady={clientReady}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        'h-full flex flex-col bg-ink-muted text-white relative',
        mobile ? 'w-full' : 'w-[1200px] min-w-[1200px]',
      )}
    >
      <div className={classNames('flex-shrink-0 px-4', mobile ? 'pb-2 pt-1' : 'p-4 pb-2')}>
        {!mobile && (
          <Heading tone="inverse" size="md" className="mb-1">
            Песочная терапия
          </Heading>
        )}
        <p className="text-ui text-white/70">Выберите фигуры, которые хотите использовать</p>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4">
        <div className={classNames('space-y-4', mobile ? 'pb-3' : 'pb-[90px]')}>
          {[
            { label: 'Семья', types: ['family-elder', 'family-adult', 'family-teen', 'family-child', 'family-baby'] },
            { label: 'Эмоции', types: ['emotional-calm', 'emotional-upset', 'emotional-happy'] },
            { label: 'Окружение', types: ['house', 'tree'] },
            { label: 'Животные', types: ['animal-dog', 'animal-cat', 'animal-bird'] },
            { label: 'Специальные', types: ['cocoon', 'striped'] },
            { label: 'Люди', types: ['person', 'tall'] },
            { label: 'Шары', types: ['ball'] },
          ].map(({ label, types }) => {
            const figs = availableFigures.filter((f) => types.includes(f.type));
            return (
              <div key={label}>
                <p className="text-ui-xs text-white/60 mb-2">{label}</p>
                <div className="grid grid-cols-4 gap-2">
                  {figs.map((fig, idx) => {
                    const selected = isFigureSelected(fig);
                    return (
                      <Pressable
                        key={idx}
                        aria-label={fig.name}
                        aria-pressed={selected}
                        onClick={() => toggleFigure(fig)}
                        className={classNames(
                          'flex items-center justify-center p-2 rounded-control border-2 transition-all',
                          selected ? 'border-accent bg-accent/20' : 'border-white/20 hover:border-white/40 bg-white/5',
                        )}
                        title={fig.name}
                      >
                        <div className={classNames('w-8', getFigureHeightClass(fig.size))}>
                          <FigureRenderer figure={fig} className="w-full h-full" />
                        </div>
                      </Pressable>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div
        className={classNames(
          'p-2 bg-ink-muted',
          mobile ? 'shrink-0 border-t border-white/10 pb-3' : 'absolute bottom-0 left-0 right-0',
        )}
      >
        <div className="text-ui-xs text-white/60 mb-2 text-center">Выбрано: {selectedFigures.length}</div>
        <Button size="lg" fullWidth disabled={selectedFigures.length === 0} onClick={handleStartPlacement}>
          Начать работу с фигурами
        </Button>
      </div>
    </div>
  );
};
